import { defineStore } from 'pinia';
import { getAllTaxesAPI, addTaxAPI, updateTaxAPI, deleteTaxAPI, updateTaxDefaultAPI, clearDefaultTaxAPI } from '@/utils/api';

export interface ServiceChargeDestination {
    id: String;
    name: String;
}

export interface viviopos_taxes {
    MID: number;
    ID: String;
    No?: String | null;
    Name?: String | null;
    Type?: String | null;
    Rate?: number | null;
    RateType?: String | null;
    Threshold?: number | null;
    Created?: number | null;
    Modified?: number | null;
    Destinations?: string | null;
    IsServiceCharge?: boolean | null;
    IncludeServiceCharge?: boolean | null;
    IsDefault: boolean;
}

export interface TaxDetail {
    id: String;
    no: String;
    name: String;
    type: String;
    serviceCharge: boolean;
    includeServiceCharge: boolean;
    rate: number;
    rateType: String;
    Threshold: number;
    default: boolean;
    serviceChargeDestinations: ServiceChargeDestination[];
}

export function taxDetailToViviposTax(tax: TaxDetail): viviopos_taxes {
    return {
        MID: 1,
        ID: tax.id,
        No: tax.no,
        Name: tax.name,
        Type: tax.type,
        Rate: tax.rate,
        RateType: tax.rateType,
        Threshold: tax.Threshold,
        Destinations: tax.serviceChargeDestinations && tax.serviceChargeDestinations.length > 0
            ? tax.serviceChargeDestinations.map(dest => String(dest.id)).join('|')
            : null,
        IsServiceCharge: tax.serviceCharge,
        IncludeServiceCharge: tax.includeServiceCharge,
        IsDefault: tax.default,
    };
}

export function viviposTaxToTaxDetail(tax: viviopos_taxes): TaxDetail {
    return {
        id: tax.ID,
        no: tax.No ?? '',
        name: tax.Name ?? '',
        type: tax.Type ?? '',
        serviceCharge: tax.IsServiceCharge ?? false,
        includeServiceCharge: tax.IncludeServiceCharge ?? false,
        rate: tax.Rate ?? 0,
        rateType: tax.RateType ?? '',
        Threshold: tax.Threshold ?? 0,
        default: tax.IsDefault,
        serviceChargeDestinations: tax.Destinations
            ? tax.Destinations.split('|').map((id: string) => ({ id: String(id), name: '' }))
            : [],
    };
}

export const defaultTax = {
    id: '',
    no: '',
    name: '',
    type: '',
    serviceCharge: false,
    includeServiceCharge: false,
    rate: 0,
    rateType: '',
    Threshold: 0,
    default: false,
    serviceChargeDestinations: [],
} as TaxDetail

export const useTaxesStore = defineStore('taxes', {
    state: () => ({
        taxDetailsList: [] as TaxDetail[],
        activeTax: {} as TaxDetail,
    }),
    getters: {
        getActiveTax: (state) => {
            return state.activeTax;
        },
        findTaxById: (state) => (id: string) => {
            return state.taxDetailsList.find(tax => tax.id === id);
        },
        getTaxesList: (state) => {
            return state.taxDetailsList;
        }
    },
    actions: {
        initializeTaxes() {
            getAllTaxesAPI().then((taxes: TaxDetail[]) => {
                this.taxDetailsList = taxes;
            }).catch((error: Error) => {
                console.error('Error fetching categories:', error);
            });
        },
        addTax(tax: TaxDetail) {
            addTaxAPI(tax).then((response) => {
                this.taxDetailsList.push(tax);
                getAllTaxesAPI().then((taxes: TaxDetail[]) => {
                    this.taxDetailsList = taxes;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }
            ).catch((error: Error) => {
                console.error('Error adding tax:', error);
            });
        },
        removeTax(id: string) {
            deleteTaxAPI(id).then(() => {
                this.taxDetailsList = this.taxDetailsList.filter(tax => tax.id !== id);
                getAllTaxesAPI().then((taxes: TaxDetail[]) => {
                    this.taxDetailsList = taxes;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error: Error) => {
                console.error('Error deleting tax:', error);
            });
        },
        updateTax(updatedTax: TaxDetail) {
            updateTaxAPI(updatedTax).then((response) => {
                const index = this.taxDetailsList.findIndex(tax => tax.id === updatedTax.id);
                if (index !== -1) {
                    this.taxDetailsList[index] = updatedTax;
                }
                getAllTaxesAPI().then((taxes: TaxDetail[]) => {
                    this.taxDetailsList = taxes;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error: Error) => {
                console.error('Error updating tax:', error);
            });
        },
        setActiveTax(tax: TaxDetail) {
            this.activeTax = tax;
        },
        setDefaultTax(id: string) {
            updateTaxDefaultAPI(id).then(() => {
                getAllTaxesAPI().then((taxes: TaxDetail[]) => {
                    this.taxDetailsList = taxes;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error: Error) => {
                return console.error('Error setting default tax:', error);
            });
        },
        clearDefaultTax(id: string) {
            clearDefaultTaxAPI(id).then(() => {
                getAllTaxesAPI().then((taxes: TaxDetail[]) => {
                    this.taxDetailsList = taxes;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error: Error) => {
                return console.error('Error clearing default tax:', error);
            });
        }
    },
});